/**
 * Permission Constants
 * 
 * Centralized list of all system permissions.
 * These should match the permissions defined in the backend.
 */

export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_EXPORT: 'dashboard.export',

  // Users
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  USERS_MANAGE_PERMISSIONS: 'users.manage_permissions',

  // Policies
  POLICIES_VIEW: 'policies.view',
  POLICIES_CREATE: 'policies.create',
  POLICIES_UPDATE: 'policies.update',
  POLICIES_DELETE: 'policies.delete',
  POLICIES_ACTIVATE: 'policies.activate',
  POLICIES_VOID: 'policies.void',
  POLICIES_UPDATE_PAYMENT: 'policies.update_payment',
  POLICIES_EXPORT: 'policies.export',
  POLICIES_BULK_UPLOAD: 'policies.bulk_upload',

  // Documents
  DOCUMENTS_VIEW: 'documents.view',
  DOCUMENTS_GENERATE: 'documents.generate',
  DOCUMENTS_REGENERATE: 'documents.regenerate',
  DOCUMENTS_DOWNLOAD: 'documents.download',
  DOCUMENTS_EXPORT: 'documents.export',

  // Claims
  CLAIMS_VIEW: 'claims.view',
  CLAIMS_CREATE: 'claims.create',
  CLAIMS_UPDATE: 'claims.update',
  CLAIMS_UPDATE_STATUS: 'claims.update_status',
  CLAIMS_EXPORT: 'claims.export',

  // Companies
  COMPANIES_VIEW: 'companies.view',
  COMPANIES_CREATE: 'companies.create',
  COMPANIES_UPDATE: 'companies.update',
  COMPANIES_DELETE: 'companies.delete',

  // Branches
  BRANCHES_VIEW: 'branches.view',
  BRANCHES_CREATE: 'branches.create',
  BRANCHES_UPDATE: 'branches.update',
  BRANCHES_DELETE: 'branches.delete',

  // Governing Bodies
  GOVERNING_BODIES_VIEW: 'governing_bodies.view',
  GOVERNING_BODIES_CREATE: 'governing_bodies.create',
  GOVERNING_BODIES_UPDATE: 'governing_bodies.update',
  GOVERNING_BODIES_DELETE: 'governing_bodies.delete',

  // Reports
  REPORTS_VIEW: 'reports.view',
  REPORTS_EXPORT: 'reports.export',

  // Audit Logs
  AUDIT_LOGS_VIEW: 'audit_logs.view',
  AUDIT_LOGS_EXPORT: 'audit_logs.export',

  // System
  SYSTEM_VIEW_HEALTH: 'system.view_health',
  SYSTEM_MANAGE_SETTINGS: 'system.manage_settings',

  // Car Data
  CAR_DATA_VIEW: 'car_data.view',
  CAR_DATA_MANAGE: 'car_data.manage',

  // Roles
  ROLES_VIEW: 'roles.view',
  ROLES_CREATE: 'roles.create',
  ROLES_UPDATE: 'roles.update',
  ROLES_DELETE: 'roles.delete',

  // Insurance Types (Commission System)
  INSURANCE_TYPES_VIEW: 'insurance_types.view',
  INSURANCE_TYPES_CREATE: 'insurance_types.create',
  INSURANCE_TYPES_UPDATE: 'insurance_types.update',
  INSURANCE_TYPES_DELETE: 'insurance_types.delete',
  INSURANCE_TYPES_MANAGE_COMMISSIONS: 'insurance_types.manage_commissions',

  // Commissions
  COMMISSIONS_VIEW: 'commissions.view',
  COMMISSIONS_VIEW_ALL: 'commissions.view_all',
  COMMISSIONS_EXPORT: 'commissions.export',

  // Emails
  EMAILS_CONFIGURE: 'emails.configure',
  EMAILS_VIEW_HISTORY: 'emails.view_history',
  EMAILS_SEND_TEST: 'emails.send_test',
  EMAILS_MANAGE_SCHEDULES: 'emails.manage_schedules',
};

/**
 * Permission groups for display in UI
 */
export const PERMISSION_GROUPS = {
  dashboard: {
    name: 'Dashboard',
    description: 'Dashboard access and data export',
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.DASHBOARD_EXPORT,
    ]
  },
  users: {
    name: 'User Management',
    description: 'User creation, modification, and permission management',
    permissions: [
      PERMISSIONS.USERS_VIEW,
      PERMISSIONS.USERS_CREATE,
      PERMISSIONS.USERS_UPDATE,
      PERMISSIONS.USERS_DELETE,
      PERMISSIONS.USERS_MANAGE_PERMISSIONS,
    ]
  },
  policies: {
    name: 'Policy Management',
    description: 'Insurance policy operations',
    permissions: [
      PERMISSIONS.POLICIES_VIEW,
      PERMISSIONS.POLICIES_CREATE,
      PERMISSIONS.POLICIES_UPDATE,
      PERMISSIONS.POLICIES_DELETE,
      PERMISSIONS.POLICIES_ACTIVATE,
      PERMISSIONS.POLICIES_VOID,
      PERMISSIONS.POLICIES_UPDATE_PAYMENT,
      PERMISSIONS.POLICIES_EXPORT,
      PERMISSIONS.POLICIES_BULK_UPLOAD,
    ]
  },
  documents: {
    name: 'Document Management',
    description: 'Document generation and management',
    permissions: [
      PERMISSIONS.DOCUMENTS_VIEW,
      PERMISSIONS.DOCUMENTS_GENERATE,
      PERMISSIONS.DOCUMENTS_REGENERATE,
      PERMISSIONS.DOCUMENTS_DOWNLOAD,
      PERMISSIONS.DOCUMENTS_EXPORT,
    ]
  },
  claims: {
    name: 'Claims Management',
    description: 'Insurance claims processing',
    permissions: [
      PERMISSIONS.CLAIMS_VIEW,
      PERMISSIONS.CLAIMS_CREATE,
      PERMISSIONS.CLAIMS_UPDATE,
      PERMISSIONS.CLAIMS_UPDATE_STATUS,
      PERMISSIONS.CLAIMS_EXPORT,
    ]
  },
  companies: {
    name: 'Company Management',
    description: 'Company creation and management',
    permissions: [
      PERMISSIONS.COMPANIES_VIEW,
      PERMISSIONS.COMPANIES_CREATE,
      PERMISSIONS.COMPANIES_UPDATE,
      PERMISSIONS.COMPANIES_DELETE,
    ]
  },
  branches: {
    name: 'Branch Management',
    description: 'Branch creation and management',
    permissions: [
      PERMISSIONS.BRANCHES_VIEW,
      PERMISSIONS.BRANCHES_CREATE,
      PERMISSIONS.BRANCHES_UPDATE,
      PERMISSIONS.BRANCHES_DELETE,
    ]
  },
  governing_bodies: {
    name: 'Governing Bodies',
    description: 'Governing body management',
    permissions: [
      PERMISSIONS.GOVERNING_BODIES_VIEW,
      PERMISSIONS.GOVERNING_BODIES_CREATE,
      PERMISSIONS.GOVERNING_BODIES_UPDATE,
      PERMISSIONS.GOVERNING_BODIES_DELETE,
    ]
  },
  reports: {
    name: 'Reports',
    description: 'Report generation and export',
    permissions: [
      PERMISSIONS.REPORTS_VIEW,
      PERMISSIONS.REPORTS_EXPORT,
    ]
  },
  audit_logs: {
    name: 'Audit Logs',
    description: 'System audit log access',
    permissions: [
      PERMISSIONS.AUDIT_LOGS_VIEW,
      PERMISSIONS.AUDIT_LOGS_EXPORT,
    ]
  },
  system: {
    name: 'System Administration',
    description: 'System configuration and monitoring',
    permissions: [
      PERMISSIONS.SYSTEM_VIEW_HEALTH,
      PERMISSIONS.SYSTEM_MANAGE_SETTINGS,
    ]
  },
  car_data: {
    name: 'Car Data',
    description: 'Car makes and models management',
    permissions: [
      PERMISSIONS.CAR_DATA_VIEW,
      PERMISSIONS.CAR_DATA_MANAGE,
    ]
  },
  roles: {
    name: 'Role Management',
    description: 'Role and permission management',
    permissions: [
      PERMISSIONS.ROLES_VIEW,
      PERMISSIONS.ROLES_CREATE,
      PERMISSIONS.ROLES_UPDATE,
      PERMISSIONS.ROLES_DELETE,
    ]
  },
  insurance_types: {
    name: 'Insurance Types',
    description: 'Insurance type and commission management',
    permissions: [
      PERMISSIONS.INSURANCE_TYPES_VIEW,
      PERMISSIONS.INSURANCE_TYPES_CREATE,
      PERMISSIONS.INSURANCE_TYPES_UPDATE,
      PERMISSIONS.INSURANCE_TYPES_DELETE,
      PERMISSIONS.INSURANCE_TYPES_MANAGE_COMMISSIONS,
    ]
  },
  commissions: {
    name: 'Commission Reports',
    description: 'Commission tracking and reporting',
    permissions: [
      PERMISSIONS.COMMISSIONS_VIEW,
      PERMISSIONS.COMMISSIONS_VIEW_ALL,
      PERMISSIONS.COMMISSIONS_EXPORT,
    ]
  },
  emails: {
    name: 'Email System',
    description: 'Email configuration and management',
    permissions: [
      PERMISSIONS.EMAILS_CONFIGURE,
      PERMISSIONS.EMAILS_VIEW_HISTORY,
      PERMISSIONS.EMAILS_SEND_TEST,
      PERMISSIONS.EMAILS_MANAGE_SCHEDULES,
    ]
  },
};

/**
 * Helper function to get permission display name
 */
export function getPermissionDisplayName(permissionName) {
  const parts = permissionName.split('.');
  const resource = parts[0].replace(/_/g, ' ');
  const action = parts[1].replace(/_/g, ' ');
  return `${resource.charAt(0).toUpperCase() + resource.slice(1)}: ${action.charAt(0).toUpperCase() + action.slice(1)}`;
}

/**
 * Helper function to check if user has permission
 * (This will be enhanced when we add PermissionContext)
 */
export function hasPermission(userPermissions, requiredPermission) {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  // Admin wildcard check
  if (userPermissions.includes('*')) {
    return true;
  }
  return userPermissions.includes(requiredPermission);
}

/**
 * Helper to check if user has any of the permissions
 */
export function hasAnyPermission(userPermissions, requiredPermissions) {
  return requiredPermissions.some(perm => hasPermission(userPermissions, perm));
}

/**
 * Helper to check if user has all of the permissions
 */
export function hasAllPermissions(userPermissions, requiredPermissions) {
  return requiredPermissions.every(perm => hasPermission(userPermissions, perm));
}

